/**
 * Hero Banner Dynamic - Review Popup
 */

/* HVAC-AI-059 */
class HbdReviewPopup {
  constructor() {
    this.popup = document.getElementById('hbd-review-popup');
    if (!this.popup) return;

    /* Detach from section so position:fixed is not trapped by parent transform/overflow (Shopify sections). */
    if (this.popup.parentNode && this.popup.parentNode !== document.body) {
      document.body.appendChild(this.popup);
    }

    this.init();
    this._reviewsBadgeRevealDone = false;
    this.startReviewsBadgeSync();
  }

  /** Walk light DOM + open shadow roots (Elfsight often nests shadow). */
  _walkElements(root, visit) {
    if (!root) return;
    const stack = [root];
    while (stack.length) {
      const el = stack.pop();
      if (!el || el.nodeType !== Node.ELEMENT_NODE) continue;
      visit(el);
      for (let i = el.children.length - 1; i >= 0; i--) stack.push(el.children[i]);
      if (el.shadowRoot) {
        for (let i = el.shadowRoot.children.length - 1; i >= 0; i--) {
          stack.push(el.shadowRoot.children[i]);
        }
      }
    }
  }

  /** Read Elfsight header rating + reviews count from the same embed as the popup. */
  _readElfsightHeaderStats() {
    const mount = this.popup.querySelector('.hbd-review-popup__widget');
    if (!mount) return null;

    let ratingEl = null;
    let countEl = null;
    let ariaRatingEl = null;

    this._walkElements(mount, (el) => {
      const cls = el.getAttribute?.('class') || '';
      if (!ratingEl && cls.includes('es-rating-value')) ratingEl = el;
      if (!countEl && cls.includes('es-header-rating-reviews-count')) countEl = el;
      if (!ariaRatingEl && el.getAttribute?.('role') === 'img') {
        const label = el.getAttribute?.('aria-label') || '';
        if (/rating:\s*[\d.]+\s*out of\s*5/i.test(label)) ariaRatingEl = el;
      }
    });

    let ratingText = (ratingEl?.textContent || '').replace(/\s+/g, ' ').trim();
    if (!ratingText && ariaRatingEl) {
      const m = (ariaRatingEl.getAttribute('aria-label') || '').match(
        /rating:\s*([\d.,]+)\s*out of\s*5/i
      );
      if (m) ratingText = m[1].replace(',', '.');
    }

    const rawCount = (countEl?.textContent || '').replace(/\s+/g, '').trim();
    const countDigits = rawCount.replace(/[^\d]/g, '');

    if (!ratingText && !countDigits) return null;

    let ratingNum = parseFloat(ratingText.replace(',', '.'));
    if (Number.isNaN(ratingNum) || ratingNum < 0 || ratingNum > 5) {
      ratingNum = null;
    }

    const reviewCount = countDigits ? parseInt(countDigits, 10) : NaN;
    const countOk = !Number.isNaN(reviewCount) && reviewCount >= 0;

    if (ratingNum === null && !countOk) return null;

    const ratingStr =
      ratingNum !== null ? ratingText.replace(/\s+/g, '').replace(',', '.') : null;

    return {
      ratingStr,
      ratingNum,
      reviewCount: countOk ? reviewCount : null,
    };
  }

  _finishBadgeSync() {
    if (this._badgeFallbackTimer) {
      clearTimeout(this._badgeFallbackTimer);
      this._badgeFallbackTimer = null;
    }
    if (this._badgeTimers) {
      this._badgeTimers.forEach((id) => clearTimeout(id));
      this._badgeTimers = [];
    }
    if (this._badgeObserver) {
      this._badgeObserver.disconnect();
      this._badgeObserver = null;
    }
  }

  _revealReviewsBadge(badge, starsRow) {
    if (!badge || this._reviewsBadgeRevealDone) return;
    this._reviewsBadgeRevealDone = true;
    badge.removeAttribute('data-hbd-reviews-awaiting');
    if (starsRow) starsRow.removeAttribute('aria-busy');
    this._finishBadgeSync();
  }

  _applyFallbackBadgeAccessibility(badge) {
    const starsRow = badge?.querySelector('.hbd__rating-stars');
    const scoreEl = badge?.querySelector('.hbd__rating-score');
    const countEl = badge?.querySelector('.hbd__review-count');
    if (!starsRow || !scoreEl || !countEl) return;
    const n = (countEl.textContent || '').replace(/[^\d]/g, '');
    const r = scoreEl.textContent.trim();
    if (!r || !n) return;
    starsRow.setAttribute('role', 'img');
    starsRow.setAttribute('aria-label', `Rating: ${r} out of 5, ${n} reviews`);
  }

  syncReviewsBadgeFromElfsight() {
    if (!this.popup || this._reviewsBadgeRevealDone) return;

    const stats = this._readElfsightHeaderStats();
    if (!stats) return;

    const badge = document.querySelector('.hbd__reviews');
    if (!badge) return;

    const scoreEl = badge.querySelector('.hbd__rating-score');
    const countEl = badge.querySelector('.hbd__review-count');
    const starsRow = badge.querySelector('.hbd__rating-stars');

    if (stats.ratingStr && scoreEl) scoreEl.textContent = stats.ratingStr;
    if (stats.reviewCount !== null && countEl) countEl.textContent = `(${stats.reviewCount})`;

    const ratingDone = Boolean(
      stats.ratingStr && scoreEl && scoreEl.textContent.trim() === stats.ratingStr
    );
    const countDone = Boolean(
      stats.reviewCount !== null &&
        countEl &&
        countEl.textContent.replace(/\s+/g, '') === `(${stats.reviewCount})`
    );

    if (ratingDone && countDone) {
      if (starsRow) {
        starsRow.setAttribute('role', 'img');
        starsRow.setAttribute(
          'aria-label',
          `Rating: ${stats.ratingStr} out of 5, ${stats.reviewCount} reviews`
        );
      }
      this._revealReviewsBadge(badge, starsRow);
    }
  }

  startReviewsBadgeSync() {
    this.syncReviewsBadgeFromElfsight();

    this._badgeTimers = this._badgeTimers || [];
    [0, 50, 120, 250, 500, 900, 1500, 2500, 4000, 6500, 9500].forEach((ms) => {
      this._badgeTimers.push(
        setTimeout(() => {
          if (!this._reviewsBadgeRevealDone) this.syncReviewsBadgeFromElfsight();
        }, ms)
      );
    });

    this._badgeFallbackTimer = setTimeout(() => {
      if (this._reviewsBadgeRevealDone) return;
      const badge = document.querySelector('.hbd__reviews');
      this._applyFallbackBadgeAccessibility(badge);
      this._revealReviewsBadge(badge, badge?.querySelector('.hbd__rating-stars'));
    }, 12000);

    if (!this._badgeObserver) {
      this._badgeObserver = new MutationObserver(() => {
        if (!this._reviewsBadgeRevealDone) {
          this.syncReviewsBadgeFromElfsight();
        }
      });
      const mount = this.popup.querySelector('.hbd-review-popup__widget');
      if (mount) {
        this._badgeObserver.observe(mount, { childList: true, subtree: true, characterData: true });
      }
    }
  }

  /** Real element for click (text nodes have no .closest; shadow DOM needs composedPath). */
  _eventTargetElement(e) {
    const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
    for (let i = 0; i < path.length; i++) {
      const n = path[i];
      if (n && n.nodeType === Node.ELEMENT_NODE && typeof n.closest === 'function') {
        return n;
      }
    }
    const t = e.target;
    if (!t) return null;
    if (t.nodeType === Node.ELEMENT_NODE) return t;
    if (t.nodeType === Node.TEXT_NODE) return t.parentElement;
    return null;
  }

  /**
   * Clicks on Elfsight’s “Read more” (e.g. .es-text-shortener-control, role=button).
   */
  _isElfsightReadMoreClick(target) {
    if (!target || !this.popup.contains(target)) return false;

    /* Current Elfsight DOM: div.es-text-shortener-control[role="button"] (same node may become “Read less” when expanded) */
    const shortenerControl = target.closest?.(
      '[class*="es-text-shortener-control"], [class*="TextShortenerControl"]'
    );
    if (shortenerControl && this.popup.contains(shortenerControl)) {
      const label = (shortenerControl.textContent || '').replace(/\s+/g, ' ').trim();
      if (/^(read more|leer más|ver más)$/i.test(label)) return true;
    }

    const shortener = target.closest?.(
      '[class*="es-text-shortener"], [class*="TextShortener"], [class*="es-text-shortener-container"]'
    );
    if (!shortener) return false;

    const byClass = target.closest?.(
      '[class*="ReadMore"], [class*="read-more"], [class*="ReadMoreText"], [class*="es-read-more"]'
    );
    if (byClass && shortener.contains(byClass)) return true;

    const control = target.closest?.('button, a, [role="button"]');
    if (control && shortener.contains(control)) {
      const t = (control.textContent || '').replace(/\s+/g, ' ').trim();
      if (/^(read more|leer más|ver más)$/i.test(t)) return true;
    }

    let node = target.nodeType === Node.TEXT_NODE ? target.parentElement : target;
    for (let i = 0; i < 8 && node && shortener.contains(node); i++) {
      const t = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (t.length <= 14 && /^(read more|leer más|ver más)$/i.test(t)) return true;
      node = node.parentElement;
    }

    return false;
  }

  init() {
    /* Capture phase + delegation: reliable on mobile Safari (nested nodes, late paint, tap targets). */
    this._onDocumentPointer = (e) => {
      const trigger = this._eventTargetElement(e)?.closest?.('[data-hbd-review-popup-trigger]');
      if (trigger) {
        e.preventDefault();
        this.open();
        return;
      }

      if (!this.popup.classList.contains('active')) return;

      /* Elfsight “Read more” (full review / overlay) — close our modal so it doesn’t stack. */
      const clickEl = this._eventTargetElement(e);
      if (clickEl && this._isElfsightReadMoreClick(clickEl)) {
        setTimeout(() => this.close(), 150);
        return;
      }

      const closeEl = clickEl?.closest?.('[data-hbd-review-popup-close]');
      if (closeEl && this.popup.contains(closeEl)) {
        e.preventDefault();
        this.close();
        return;
      }

      if (
        clickEl === this.popup ||
        clickEl?.classList?.contains('hbd-review-popup__overlay')
      ) {
        this.close();
      }
    };

    document.addEventListener('click', this._onDocumentPointer, true);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('active')) {
        this.close();
      }
    });
  }

  /**
   * Hides the Elfsight widget heading "What our customers say".
   * Only targets elements whose text is exactly that phrase (avoids hiding the whole widget).
   */
  hideReviewPopupTitle() {
    const phrase = 'What our customers say';
    const norm = (s) => s.replace(/\s+/g, ' ').trim();

    const hideEl = (el) => {
      if (!el || el.closest?.('.hbd-review-popup__close')) return;
      el.style.setProperty('display', 'none', 'important');
    };

    const scanRoot = (root) => {
      if (!root || !root.querySelectorAll) return;
      root.querySelectorAll('*').forEach((el) => {
        if (norm(el.textContent) === phrase) {
          hideEl(el);
        }
        if (el.shadowRoot) {
          scanRoot(el.shadowRoot);
        }
      });
    };

    scanRoot(this.popup);
  }

  /**
   * True if this Swiper slide is the Elfsight AI summary (not a real Google review).
   * display:none breaks Swiper layout (shrunk slide / wrong height); we remove the node instead.
   */
  isAiSummarySlide(slide) {
    if (!slide?.classList?.contains('swiper-slide')) return false;
    const selectors = [
      '.es-ai-summary-author-block-container',
      '[class*="AISummaryAuthorBlock"]',
      '[class*="AISummaryContent"]',
      '.es-ai-summary-avatar-icon',
    ];
    if (selectors.some((sel) => slide.querySelector(sel))) return true;
    const text = slide.textContent || '';
    if (text.includes('AI-Generated Summary')) return true;
    return false;
  }

  /**
   * Removes AI-summary slides from the DOM and refreshes Swiper so the first visible slide is a real review.
   */
  removeAiSummarySlides() {
    if (!this.popup) return;

    const slides = [...this.popup.querySelectorAll('.swiper-slide')].filter((s) =>
      this.isAiSummarySlide(s)
    );
    if (!slides.length) return;

    const seenSwipers = new Set();

    slides.forEach((slide) => {
      const swiperEl = slide.closest('.swiper');
      slide.remove();
      if (swiperEl) seenSwipers.add(swiperEl);
    });

    const getSwiper = (el) => el.swiper || el._swiper || el.__swiper__;

    seenSwipers.forEach((swiperEl) => {
      const sw = getSwiper(swiperEl);
      if (!sw) return;
      try {
        sw.update();
        sw.slideTo(0, 0);
        if (typeof sw.updateSize === 'function') sw.updateSize();
        if (typeof sw.updateSlides === 'function') sw.updateSlides();
      } catch (e) {
        /* ignore */
      }
    });

    requestAnimationFrame(() => {
      seenSwipers.forEach((swiperEl) => {
        const sw = getSwiper(swiperEl);
        if (!sw) return;
        try {
          sw.update();
          sw.slideTo(0, 0);
        } catch (e) {
          /* ignore */
        }
      });
    });
  }

  /**
   * Match reference layout: heading reads "Google Rating" instead of lowercase "reviews".
   */
  normalizeReviewPopupHeader() {
    if (!this.popup) return;
    const heading = this.popup.querySelector('[class*="es-header-heading-text"]');
    if (!heading) return;
    const t = heading.textContent.trim();
    if (/^reviews$/i.test(t)) heading.textContent = 'Google Rating';
  }

  runEmbedLayoutFixes() {
    this.syncReviewsBadgeFromElfsight();
    this.hideReviewPopupTitle();
    this.removeAiSummarySlides();
    this.normalizeReviewPopupHeader();
  }

  startReviewTitleCleanup() {
    this.runEmbedLayoutFixes();
    this._titleTimers = this._titleTimers || [];
    /* Elfsight injects Swiper slides after paint; retry until AI slide is gone */
    [200, 600, 1200, 2500, 4000, 6000].forEach((ms) => {
      this._titleTimers.push(setTimeout(() => this.runEmbedLayoutFixes(), ms));
    });
    if (!this._titleObserver) {
      this._titleObserver = new MutationObserver(() => this.runEmbedLayoutFixes());
      this._titleObserver.observe(this.popup, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  stopReviewTitleCleanup() {
    if (this._titleTimers) {
      this._titleTimers.forEach((id) => clearTimeout(id));
      this._titleTimers = [];
    }
    if (this._titleObserver) {
      this._titleObserver.disconnect();
      this._titleObserver = null;
    }
  }

  open() {
    this.popup.style.display = 'block';
    this.popup.offsetHeight;
    this.popup.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.startReviewTitleCleanup();
    // Let Elfsight/Swiper recalc slide widths after popup is visible (mobile full-width slides).
    const nudgeLayout = () => {
      window.dispatchEvent(new Event('resize'));
    };
    requestAnimationFrame(nudgeLayout);
    setTimeout(nudgeLayout, 150);
    setTimeout(nudgeLayout, 400);
  }

  close() {
    this.stopReviewTitleCleanup();
    this.syncReviewsBadgeFromElfsight();
    this.popup.classList.remove('active');
    setTimeout(() => {
      this.popup.style.display = 'none';
      document.body.style.overflow = '';
    }, 300);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new HbdReviewPopup();
  });
} else {
  new HbdReviewPopup();
}
